import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ArrowLeft, ExternalLink, Image as ImageIcon, FileText, Users, HardDrive, Home, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const collectionMeta = {
  hero_slides: { label: 'Imágenes de Inicio', icon: Home, path: '/admin/hero' },
  blog_posts: { label: 'Blog', icon: FileText, path: '/admin/blog' },
  galleries: { label: 'Galerías públicas', icon: ImageIcon, path: '/admin/galleries' },
  testimonials: { label: 'Testimonios', icon: MessageSquare, path: '/admin/testimonials' },
  private_files: { label: 'Archivos privados', icon: HardDrive, path: '/admin/private-files' },
  services: { label: 'Servicios', icon: HardDrive, path: '/admin/services' },
  shared_documents: { label: 'Documentos compartidos', icon: FileText, path: '/admin/shared-documents' },
};

export const AdminBrokenImages = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await adminFetch(`${API}/admin/cloudinary/broken-scan`);
      if (res.ok) {
        setData(await res.json());
        toast.success('Escaneo completado');
      } else {
        toast.error('Error en el escaneo');
      }
    } catch {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  useEffect(() => {
    runScan();
  }, []);

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-muted rounded" />
          <div className="h-24 bg-muted rounded" />
          <div className="h-40 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" />
          Volver al dashboard
        </Link>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl mb-1">Imágenes rotas</h1>
            <p className="text-sm text-muted-foreground">
              Detecta URLs Cloudinary que apuntan a cuentas antiguas y necesitan re-subida.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={runScan}
            disabled={scanning}
            className="gap-2"
            data-testid="rescan-btn"
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Escaneando...' : 'Volver a escanear'}
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Cuenta actual</p>
            <p className="font-mono text-sm font-medium">{data?.current_cloud_name}</p>
          </div>
          <div className={`bg-card border p-4 ${data?.total_broken > 0 ? 'border-red-500/40' : 'border-green-500/40'}`}>
            <p className="text-xs text-muted-foreground mb-1">Total URLs rotas</p>
            <p className={`font-serif text-3xl ${data?.total_broken > 0 ? 'text-red-500' : 'text-green-500'}`} data-testid="total-broken">
              {data?.total_broken || 0}
            </p>
          </div>
          <div className="bg-card border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Cuentas anteriores detectadas</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {data?.other_cloud_names_found?.length > 0 ? (
                data.other_cloud_names_found.map((c) => (
                  <code key={c} className="text-xs bg-muted px-2 py-0.5 rounded">{c}</code>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">Ninguna</span>
              )}
            </div>
          </div>
        </div>

        {data?.total_broken === 0 ? (
          <div className="bg-green-500/10 border border-green-500/30 p-6 text-center" data-testid="all-clean">
            <p className="text-green-600 dark:text-green-400 font-medium">Todo en orden</p>
            <p className="text-sm text-muted-foreground mt-1">
              Todas las URLs Cloudinary apuntan a tu cuenta actual.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 mb-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">Estas imágenes no se cargarán</p>
                <p className="text-muted-foreground">
                  Están alojadas en una cuenta de Cloudinary que ya no está conectada. Ve a cada sección
                  desde los enlaces de abajo y vuelve a subir el contenido afectado.
                </p>
              </div>
            </div>

            {data?.sections?.map((section) => {
              const meta = collectionMeta[section.collection] || { label: section.collection, icon: ImageIcon, path: '/admin' };
              const Icon = meta.icon;
              return (
                <div key={section.collection} className="bg-card border border-border mb-4" data-testid={`section-${section.collection}`}>
                  <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-muted rounded flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-medium">{meta.label}</h3>
                        <p className="text-xs text-red-500">{section.count} URL{section.count === 1 ? '' : 's'} rotas</p>
                      </div>
                    </div>
                    <Link
                      to={meta.path}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border rounded hover:bg-muted transition-colors"
                    >
                      Ir a la sección
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>

                  <div className="divide-y divide-border max-h-96 overflow-y-auto">
                    {section.items.map((item, i) => (
                      <div key={i} className="p-3 flex items-start gap-3 text-sm hover:bg-muted/40 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.title || '(sin título)'}</p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-mono">{item.field}</span>
                            {item.index !== null && item.index !== undefined && (
                              <span className="ml-1 opacity-70">[{item.index}]</span>
                            )}
                          </p>
                          <p className="text-[10px] text-muted-foreground/70 truncate mt-1 font-mono">{item.url}</p>
                        </div>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-foreground p-1"
                          title="Abrir URL rota"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AdminBrokenImages;
